import React, { useState } from 'react';
import { Card } from '../components/ui';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOBreadcrumbs from '../components/SEOBreadcrumbs';
import { useTranslation } from '../hooks/useTranslation';
import { usePageSEO } from '../hooks/usePageSEO';
import { 
  Star,
  Check,
  Workspace,
  AttachMoney,
  TrendingUp,
  Security,
  Support
} from '../mui-icons';
import './PricingPage.css';

const PricingPage = () => {
  const { t } = useTranslation();
  const [isAnnual, setIsAnnual] = useState(false);
  
  // SEO dinâmico para esta página
  usePageSEO();

  return (
    <div className="pricing-page surface-marketing">
      <Header />
      <SEOBreadcrumbs />

      {/* Header Verde */}
      <section className="pricing-header-section">
        <div className="container">
          <div className="pricing-header-content">
            <AttachMoney sx={{ fontSize: 60 }} />
            <h1 className="pricing-main-title">{t('pricing.hero.title')}</h1>
            <p className="pricing-main-subtitle">
              {t('pricing.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      <div className="container pricing-container">
        {/* Seção de Benefícios */}
        <section className="benefits-section">
          <h2 className="benefits-title">{t('pricing.benefits.title')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <Check sx={{ fontSize: 32 }} />
              </div>
              <h3 className="benefit-title">{t('pricing.benefits.ai.title')}</h3>
              <p className="benefit-description">
                {t('pricing.benefits.ai.description')}
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <Check sx={{ fontSize: 32 }} />
              </div>
              <h3 className="benefit-title">{t('pricing.benefits.personalized.title')}</h3>
              <p className="benefit-description">
                {t('pricing.benefits.personalized.description')}
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <Check sx={{ fontSize: 32 }} />
              </div>
              <h3 className="benefit-title">{t('pricing.benefits.tracking.title')}</h3>
              <p className="benefit-description">
                {t('pricing.benefits.tracking.description')}
              </p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <Check sx={{ fontSize: 32 }} />
              </div>
              <h3 className="benefit-title">{t('pricing.benefits.support.title')}</h3>
              <p className="benefit-description">
                {t('pricing.benefits.support.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Custom Pricing Table */}
        <section className="custom-pricing-section">
          <h2 className="pricing-section-title">{t('pricing.plans.title')}</h2>
          <p className="pricing-section-subtitle">
            {t('pricing.plans.subtitle')}
          </p>
          
          {/* Toggle Switch */}
          <div className="pricing-toggle">
            <button 
              className={`toggle-option ${!isAnnual ? 'active' : ''}`}
              onClick={() => setIsAnnual(false)}
            >
              {t('pricing.plans.monthly')}
            </button>
            <button 
              className={`toggle-option ${isAnnual ? 'active' : ''}`}
              onClick={() => setIsAnnual(true)}
            >
              {t('pricing.plans.annually')}
            </button>
          </div>

          {/* Pricing Card */}
          <div className="pricing-card pn-card pn-card--marketing pn-card--marketing-static">
            <div className="pricing-card pn-card pn-card--marketing pn-card--marketing-static-header">
              <div className="pricing-logo">
                <TrendingUp sx={{ fontSize: 32, color: 'var(--primary-white)' }} />
              </div>
              <h3 className="pricing-plan-name">{t('pricing.plans.plan.name')}</h3>
            </div>
            
            <div className="pricing-card pn-card pn-card--marketing pn-card--marketing-static-body">
              <div className="pricing-price">
                <span className="price-currency">
                  {isAnnual ? t('pricing.plans.plan.annually.price') : t('pricing.plans.plan.monthly.price')}
                </span>
                <span className="price-period">
                  {isAnnual ? t('pricing.plans.plan.annually.period') : t('pricing.plans.plan.monthly.period')}
                </span>
              </div>
              
              {isAnnual && (
                <div className="pricing-savings">
                  <Star sx={{ fontSize: 16, color: 'var(--primary-green)' }} />
                  <span>{t('pricing.plans.plan.annually.savings')}</span>
                </div>
              )}

              <div className="pricing-features">
                <div className="feature-item">
                  <Check sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                  <span>{t('pricing.plans.plan.features.ai')}</span>
                </div>
                <div className="feature-item">
                  <Check sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                  <span>{t('pricing.plans.plan.features.personalized')}</span>
                </div>
                <div className="feature-item">
                  <Check sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                  <span>{t('pricing.plans.plan.features.tracking')}</span>
                </div>
                <div className="feature-item">
                  <Check sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                  <span>{t('pricing.plans.plan.features.support')}</span>
                </div>
                <div className="feature-item">
                  <Check sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                  <span>{t('pricing.plans.plan.features.freemium')}</span>
                </div>
              </div>

              <button className="pricing-button">
                {isAnnual ? t('pricing.plans.plan.annually.button') : t('pricing.plans.plan.monthly.button')}
              </button>
            </div>
          </div>
        </section>

        {/* FAQ de Preços */}
        <section className="pricing-faq-section">
          <h2 className="faq-title">{t('pricing.faq.title')}</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">
                <Star sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                {t('pricing.faq.cancel.question')}
              </h3>
              <p className="faq-answer">
                {t('pricing.faq.cancel.answer')}
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">
                <Star sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                {t('pricing.faq.freemium.question')}
              </h3>
              <p className="faq-answer">
                {t('pricing.faq.freemium.answer')}
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">
                <Star sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                {t('pricing.faq.payment.question')}
              </h3>
              <p className="faq-answer">
                {t('pricing.faq.payment.answer')}
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">
                <Star sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                {t('pricing.faq.change.question')}
              </h3>
              <p className="faq-answer">
                {t('pricing.faq.change.answer')}
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">
                <Star sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                {t('pricing.faq.security.question')}
              </h3>
              <p className="faq-answer">
                {t('pricing.faq.security.answer')}
              </p>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">
                <Star sx={{ fontSize: 20, color: 'var(--primary-green)' }} />
                {t('pricing.faq.discount.question')}
              </h3>
              <p className="faq-answer">
                {t('pricing.faq.discount.answer')}
              </p>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="pricing-cta">
          <h3 className="pricing-cta-title">{t('pricing.cta.title')}</h3>
          <p className="pricing-cta-text">
            {t('pricing.cta.subtitle')}
          </p>
          <div className="cta-buttons">
            <a href="#pricing-card" className="cta-button primary">
              {t('pricing.cta.viewPlans')}
            </a>
            <a href="/support" className="cta-button secondary">
              {t('pricing.cta.contactSupport')}
            </a>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default PricingPage;

